import { CubeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { isDocEmpty, type DataTableColumn } from '@odo/shared-ui';
import { CategoryChip } from '../CategoryChip';
import type { ProductRow, ProductVersion } from '../../../services/billing';
import { formatMoneyCents as money, isFiniteCents } from '../../../utils/money';
import { isSubRow, type ProductTableRow } from './productRows';
import { VersionsBadge } from './VersionsBadge';
import { ProductVendorCell } from './ProductVendorCell';

export type ProductsSortBy = 'name' | 'category' | 'price';

// Fixed widths: under auto layout every expand/collapse would renegotiate
// every column off the sub-rows' content and shift the whole table.
const COLUMN_WIDTHS = {
    product: '26%',
    category: '15%',
    price: '11%',
    vendorCost: '10%',
    versions: '12%',
    vendor: '12%',
    discounts: '9%',
    menu: '5%'
} as const;

const DASH = <span className="text-gray-300">-</span>;

const EXPIRED_TAG = (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
        Expired
    </span>
);

const DEFAULT_TAG = (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 shrink-0">
        Default
    </span>
);

const VERSION_ELBOW_INDENT = 'ml-11';
// The elbow's own width and gap past VERSION_ELBOW_INDENT, so the sub-header
// sits over the version labels rather than over their connectors.
const SUB_HEADER_INDENT = 'ml-16';

// The sub-table's columns mean something else than the header's, so it labels
// its own. aria-hidden: the real column headers already carry the semantics.
function subHeader(label: string) {
    return (
        <span
            aria-hidden="true"
            className="text-[10px] font-semibold uppercase tracking-wider text-gray-500"
        >
            {label}
        </span>
    );
}

// Single price when all versions agree, a range otherwise.
function priceLabel(versions: ProductVersion[]): string {
    if (versions.length === 0) return '-';
    const cents = versions.map((version) => version.priceCents);
    const min = Math.min(...cents);
    const max = Math.max(...cents);
    return min === max ? money(min) : `${money(min)} - ${money(max)}`;
}

// isFiniteCents, not `!== null`: a stale cached row can miss the field
// entirely, and `undefined` would reach Math.min and render "NaN".
function vendorCostLabel(versions: ProductVersion[]): string {
    const tracked = versions.map((version) => version.vendorCostCents).filter(isFiniteCents);
    if (tracked.length === 0) return '-';
    const min = Math.min(...tracked);
    const max = Math.max(...tracked);
    return min === max ? money(min) : `${money(min)} - ${money(max)}`;
}

function versionVendorCostLabel(version: ProductVersion): string {
    return isFiniteCents(version.vendorCostCents) ? money(version.vendorCostCents) : '-';
}

// A lone selector reads as its bare value ("Concierge"); several need their
// keys back to stay legible ("arch upper - tier 2").
function selectorsLabel(selectors: Record<string, string>): string | null {
    const entries = Object.entries(selectors);
    if (entries.length === 0) return null;
    if (entries.length === 1) return entries[0][1];
    return entries.map(([key, value]) => `${key} ${value}`).join(' - ');
}

export function minPriceCents(product: ProductRow): number {
    return product.versions.length ? Math.min(...product.versions.map((v) => v.priceCents)) : 0;
}

export function isProductExpired(product: ProductRow, now: number): boolean {
    return product.expiresAt !== null && new Date(product.expiresAt).getTime() <= now;
}

function isVersionExpired(version: ProductVersion, now: number): boolean {
    return !!version.expiresAt && new Date(version.expiresAt).getTime() <= now;
}

// The versions a product row's price ranges summarize; all of them once none is current.
function rangeVersions(versions: ProductVersion[], now: number): ProductVersion[] {
    const current = versions.filter((version) => !isVersionExpired(version, now));
    return current.length > 0 ? current : versions;
}

// Everything the storefront needs to render the product. The doc, not the
// derived marketingCopy: the server nulls that for an image-only doc.
export function isWebstoreReady(product: ProductRow): boolean {
    return Boolean(
        product.imageFileId &&
        product.marketingCopyDoc &&
        !isDocEmpty(product.marketingCopyDoc) &&
        product.versions.some((version) => version.vendorPartNumber)
    );
}

interface BuildProductColumnsArgs {
    expandedId: string | null;
    now: number;
    canEdit: boolean;
    onEdit: (id: string) => void;
    /** fileId -> signed URL, resolved by the page; columns never fetch. */
    imageUrls: Record<string, string>;
}

// Plain functions, not components: a "*Columns.tsx" file's only component
// export is column-preset render output (see practiceColumns.tsx's convention).
export function buildProductColumns({
    expandedId,
    now,
    canEdit,
    onEdit,
    imageUrls
}: BuildProductColumnsArgs): DataTableColumn<ProductTableRow, ProductsSortBy>[] {
    return [
        {
            key: 'product',
            label: 'Product',
            width: COLUMN_WIDTHS.product,
            sortable: true,
            sortKey: 'name',
            cellClassName: 'pl-6 pr-3 py-3',
            headerClassName: 'whitespace-nowrap !px-3 !pl-6',
            render: (row) => {
                if (row.kind === 'versionsHeader') {
                    return (
                        <span className={`block ${SUB_HEADER_INDENT}`}>{subHeader('Version')}</span>
                    );
                }
                if (row.kind === 'version') {
                    return (
                        <div className="flex items-center gap-2">
                            <span
                                aria-hidden="true"
                                className={`${VERSION_ELBOW_INDENT} w-3 h-3 shrink-0 rounded-bl-[3px] border-b border-l border-gray-300`}
                            />
                            <span
                                className="text-sm text-gray-700 truncate"
                                title={row.version.label}
                            >
                                {row.version.label}
                            </span>
                            {row.version.isDefault && DEFAULT_TAG}
                            {isVersionExpired(row.version, now) && EXPIRED_TAG}
                        </div>
                    );
                }
                const { product } = row;
                const isExpired = isProductExpired(product, now);
                const expiry = product.expiresAt ? new Date(product.expiresAt) : null;
                const imageUrl = product.imageFileId ? imageUrls[product.imageFileId] : undefined;
                return (
                    <div className="flex items-center gap-3">
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt=""
                                loading="lazy"
                                className="w-8 h-8 rounded-lg object-cover bg-gray-100 shrink-0"
                            />
                        ) : (
                            <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                                <CubeIcon className="h-4 w-4" />
                            </span>
                        )}
                        <div className="min-w-0">
                            <span className="flex items-center gap-2 min-w-0">
                                <span
                                    className={`text-sm font-medium truncate ${isExpired ? 'text-gray-500 line-through decoration-gray-300' : 'text-gray-800'}`}
                                    title={product.name}
                                >
                                    {product.name}
                                </span>
                                {product.sellableInWebstore ? (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 shrink-0">
                                        Webstore
                                    </span>
                                ) : (
                                    isWebstoreReady(product) && (
                                        <span
                                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-green-200 text-green-500 shrink-0"
                                            title="Has everything the storefront needs - not switched on yet"
                                        >
                                            Webstore ready
                                        </span>
                                    )
                                )}
                                {isExpired && EXPIRED_TAG}
                                {!isExpired && expiry && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 shrink-0">
                                        retires{' '}
                                        {expiry.toLocaleDateString(undefined, {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </span>
                                )}
                            </span>
                            {product.subCategory && (
                                <span
                                    className="block text-xs text-gray-400 truncate"
                                    title={product.subCategory}
                                >
                                    {product.subCategory}
                                </span>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'category',
            label: 'Category',
            width: COLUMN_WIDTHS.category,
            sortable: true,
            sortKey: 'category',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => {
                if (row.kind === 'product') return <CategoryChip category={row.product.category} />;
                if (row.kind === 'versionsHeader') return subHeader('Selectors');
                const label = selectorsLabel(row.version.selectors);
                return label ? (
                    <span className="text-[12px] text-gray-500 truncate" title={label}>
                        {label}
                    </span>
                ) : (
                    DASH
                );
            }
        },
        {
            key: 'price',
            label: 'Price',
            width: COLUMN_WIDTHS.price,
            sortable: true,
            sortKey: 'price',
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap truncate !px-3',
            render: (row) => {
                if (row.kind === 'versionsHeader') return subHeader('Price');
                return (
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                        {row.kind === 'version'
                            ? money(row.version.priceCents)
                            : priceLabel(rangeVersions(row.product.versions, now))}
                    </span>
                );
            }
        },
        {
            key: 'vendorCost',
            label: 'Vendor cost',
            width: COLUMN_WIDTHS.vendorCost,
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap truncate !px-3',
            render: (row) => {
                if (row.kind === 'versionsHeader') return subHeader('Vendor cost');
                const label =
                    row.kind === 'version'
                        ? versionVendorCostLabel(row.version)
                        : vendorCostLabel(rangeVersions(row.product.versions, now));
                return <span className="text-sm text-gray-600 tabular-nums">{label}</span>;
            }
        },
        {
            key: 'versions',
            label: 'Versions',
            width: COLUMN_WIDTHS.versions,
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => {
                if (row.kind === 'versionsHeader') return subHeader('Part no. / Bill.com');
                if (row.kind === 'product') {
                    return (
                        <VersionsBadge
                            count={row.product.versions.length}
                            expanded={row.product.id === expandedId}
                        />
                    );
                }
                // Shown whenever the version carries one: category is not the
                // webstore test anywhere else in the catalog.
                const { vendorPartNumber, billId } = row.version;
                return (
                    <span className="flex items-center gap-2 font-mono text-[11px] text-gray-500 min-w-0">
                        {vendorPartNumber && (
                            <span className="text-gray-700 truncate" title={vendorPartNumber}>
                                {vendorPartNumber}
                            </span>
                        )}
                        <span className="truncate" title={billId ?? undefined}>
                            {billId ?? '-'}
                        </span>
                    </span>
                );
            }
        },
        {
            key: 'vendor',
            label: 'Vendor',
            width: COLUMN_WIDTHS.vendor,
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap !px-3',
            getTitle: (row) => {
                if (row.kind === 'product') return row.product.vendorName ?? undefined;
                if (row.kind === 'version')
                    return (
                        row.version.orderMethod?.name ??
                        'No ordering method - no purchase order is raised when this version is charged'
                    );
                return undefined;
            },
            // The product's vendor up top; below it, the ordering method of each
            // version, which is the only level that carries one.
            render: (row) => {
                if (row.kind === 'versionsHeader') return subHeader('Ordering method');
                if (row.kind === 'product') return <ProductVendorCell product={row.product} />;
                const method = row.version.orderMethod;
                if (!method) return DASH;
                return (
                    <span className="text-gray-600 truncate">
                        {method.name}
                        {!method.isActive && <span className="text-gray-400"> (retired)</span>}
                    </span>
                );
            }
        },
        {
            key: 'discounts',
            label: 'Discounts',
            width: COLUMN_WIDTHS.discounts,
            cellClassName: 'px-3 text-sm whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            stopClickPropagation: true,
            render: (row) => {
                if (isSubRow(row)) return null;
                const { discountRules, id } = row.product;
                if (discountRules.length === 0) return DASH;
                const label = `${discountRules.length} ${discountRules.length === 1 ? 'rule' : 'rules'}`;
                return canEdit ? (
                    <button
                        type="button"
                        onClick={() => onEdit(id)}
                        className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-600"
                    >
                        {label}
                    </button>
                ) : (
                    <span className="text-gray-600">{label}</span>
                );
            }
        },
        {
            key: 'menu',
            label: '',
            width: COLUMN_WIDTHS.menu,
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap !px-3',
            stopClickPropagation: true,
            render: (row) =>
                canEdit && row.kind !== 'versionsHeader' ? (
                    <div className="flex items-center justify-end">
                        <button
                            type="button"
                            onClick={() => onEdit(row.product.id)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                            aria-label={
                                row.kind === 'version'
                                    ? `Edit ${row.product.name}, version ${row.version.label}`
                                    : 'Edit product'
                            }
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                    </div>
                ) : null
        }
    ];
}
